import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { type ColumnDef } from '@tanstack/react-table';
import { XCircle, RefreshCw, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  apiSlice,
  useGetJobsQuery,
  useGetJobCountsQuery,
  useGetJobQuery,
  useCancelJobMutation,
} from '@/store/api/apiSlice';
import { useGetAgentQueueStatsQuery } from '@/store/api/agentRuntimeApi';
import { wsClient } from '@/lib/ws';
import type { Job, JobCounts, AgentQueueStats } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

/** Queue-group tabs (epic-6 §7 / FUI-1). */
const QUEUE_GROUPS = [
  { key: 'all', label: 'All queues', match: /.*/ },
  { key: 'agent', label: 'agent.*', match: /^agent\./ },
  { key: 'bulk', label: 'bulk.*', match: /^bulk\./ },
  { key: 'file-edit', label: 'storage-file-edit', match: /^storage-file-edit/ },
  { key: 'indexer', label: 'indexer', match: /^indexer/i },
] as const;

export function JobsPage() {
  const [groupKey, setGroupKey] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Job | null>(null);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);

  const { data: jobsData, isLoading } = useGetJobsQuery({
    status: statusFilter || undefined,
    page: 1,
    pageSize: 50,
  });
  const { data: counts } = useGetJobCountsQuery(undefined);
  const { data: agentStats } = useGetAgentQueueStatsQuery();
  const [cancelJob, { isLoading: isCancelling }] = useCancelJobMutation();
  const dispatch = useDispatch();

  const activeGroup = QUEUE_GROUPS.find((g) => g.key === groupKey) ?? QUEUE_GROUPS[0];

  const allJobs = (jobsData as { data?: Job[] } | undefined)?.data ?? [];
  const jobs = statusFilter || groupKey === 'all'
    ? allJobs
    : allJobs.filter((j) => activeGroup.match.test(j.queueName ?? ''));

  const jobCounts = counts as JobCounts | undefined;
  const agentStatsObj = agentStats as AgentQueueStats | undefined;

  // SSE/WS real-time updates
  useEffect(() => {
    if (!realtimeEnabled) return;

    wsClient.connect();
    const unsub = wsClient.on('jobs:updated', () => {
      // Invalidate the Jobs tags so RTK Query refetches. The global
      // RealtimeProvider also covers this room; this page handler is kept for
      // defense in depth and correctness.
      dispatch(apiSlice.util.invalidateTags(['Job', 'AgentQueue', 'Dashboard']));
    });

    wsClient.subscribe('jobs:updates').catch(() => {
      // WS subscription optional — the global provider also subscribes.
    });

    return () => {
      unsub();
      wsClient.unsubscribe('jobs:updates').catch(() => {});
    };
  }, [realtimeEnabled, dispatch]);

  const columns: ColumnDef<Job, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Job',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">{row.original.queueName}</p>
        </div>
      ),
    },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} /> },
    {
      accessorKey: 'progress',
      header: 'Progress',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div className="h-2 w-20 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${row.original.progress}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground">{row.original.progress}%</span>
        </div>
      ),
    },
    {
      accessorKey: 'attemptsMade',
      header: 'Attempts',
      cell: ({ row }) => `${row.original.attemptsMade}/${row.original.attemptsAllowed}`,
    },
    {
      accessorKey: 'timestamp',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.timestamp), 'MMM d, HH:mm:ss'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => setSelectedJob(row.original)} title="View">
            <Eye className="h-4 w-4" />
          </Button>
          {(row.original.status === 'queued' || row.original.status === 'active') && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCancelTarget(row.original)}
              title="Cancel"
            >
              <XCircle className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  async function handleCancel() {
    if (cancelTarget) {
      await cancelJob(cancelTarget.id).unwrap();
      setCancelTarget(null);
    }
  }

  const countCards = [
    { label: 'Waiting', value: jobCounts?.waiting ?? 0, color: 'text-blue-600' },
    { label: 'Active', value: jobCounts?.active ?? 0, color: 'text-green-600' },
    { label: 'Completed', value: jobCounts?.completed ?? 0, color: 'text-emerald-600' },
    { label: 'Failed', value: jobCounts?.failed ?? 0, color: 'text-red-600' },
    { label: 'Delayed', value: jobCounts?.delayed ?? 0, color: 'text-amber-600' },
    { label: 'Paused', value: jobCounts?.paused ?? 0, color: 'text-gray-600' },
  ];

  const statusFilters = ['', 'queued', 'active', 'completed', 'failed', 'delayed', 'paused'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Jobs & Queues</h1>
          <p className="text-sm text-muted-foreground">
            BullMQ observability grouped by queue (agent.*, bulk.*, storage-file-edit, indexer)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={realtimeEnabled ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRealtimeEnabled(!realtimeEnabled)}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${realtimeEnabled ? 'animate-spin' : ''}`} />
            {realtimeEnabled ? 'Live' : 'Paused'}
          </Button>
        </div>
      </div>

      {/* Queue group tabs */}
      <Tabs value={groupKey} onValueChange={setGroupKey}>
        <TabsList>
          {QUEUE_GROUPS.map((g) => (
            <TabsTrigger key={g.key} value={g.key}>{g.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Aggregate counts */}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {countCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="p-4 text-center">
              <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              <p className="text-xs text-muted-foreground">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Agent queue group stats (agent-runtime GET /agent/queues/stats) */}
      {groupKey === 'agent' && agentStatsObj && (
        <ScrollableStats stats={agentStatsObj} />
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 flex-wrap">
        {statusFilters.map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {s || 'All'}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : jobs.length === 0 ? (
            <EmptyState title="No jobs found" description="Jobs will appear here when queued" />
          ) : (
            <DataTable columns={columns} data={jobs} />
          )}
        </CardContent>
      </Card>

      {/* Job detail dialog */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Job Details</DialogTitle>
            <DialogDescription>
              {selectedJob?.name} — {selectedJob?.id}
            </DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <StatusBadge status={selectedJob.status} />
                </div>
                <div>
                  <span className="text-muted-foreground">Progress:</span> {selectedJob.progress}%
                </div>
                <div>
                  <span className="text-muted-foreground">Queue:</span> {selectedJob.queueName}
                </div>
                <div>
                  <span className="text-muted-foreground">Attempts:</span>{' '}
                  {selectedJob.attemptsMade}/{selectedJob.attemptsAllowed}
                </div>
                {selectedJob.processedOn && (
                  <div>
                    <span className="text-muted-foreground">Started:</span>{' '}
                    {formatDistanceToNow(new Date(selectedJob.processedOn), { addSuffix: true })}
                  </div>
                )}
                {selectedJob.finishedOn && (
                  <div>
                    <span className="text-muted-foreground">Finished:</span>{' '}
                    {formatDistanceToNow(new Date(selectedJob.finishedOn), { addSuffix: true })}
                  </div>
                )}
              </div>
              {selectedJob.failedReason && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {selectedJob.failedReason}
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2">Job Data</p>
                <JsonViewer data={selectedJob.data} />
              </div>
              {selectedJob.result !== undefined && (
                <div>
                  <p className="text-sm font-medium mb-2">Result</p>
                  <JsonViewer data={selectedJob.result} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel Job"
        description={`Are you sure you want to cancel job "${cancelTarget?.name}" (${cancelTarget?.id})?`}
        confirmLabel="Cancel Job"
        variant="destructive"
        onConfirm={handleCancel}
        isLoading={isCancelling}
      />
    </div>
  );
}

/** Per-queue-group count strip (agent-worker / agent-runtime stats endpoint). */
function ScrollableStats({ stats }: { stats: AgentQueueStats }) {
  const groups = Object.entries(stats).filter(([, v]) => v && typeof v === 'object');
  if (groups.length === 0) return null;
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {groups.map(([queueName, c]) => {
        const counts = c as unknown as JobCounts;
        return (
          <Card key={queueName}>
            <CardHeader className="pb-1 pt-3 px-3">
              <CardTitle className="text-xs font-mono truncate">{queueName}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0">
              <div className="flex gap-3 text-[11px]">
                <span>⌛ {counts.waiting ?? 0}</span>
                <span className="text-green-600">● {counts.active ?? 0}</span>
                <span className="text-emerald-600">✓ {counts.completed ?? 0}</span>
                <span className="text-red-600">✗ {counts.failed ?? 0}</span>
                <span className="text-amber-600">◷ {counts.delayed ?? 0}</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}